/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { api } from "core/api";
import { Button, Card, FieldGroup, Form, Input, Select } from "core/components";
import { Action, ItemInventorySchema } from "core/schema";
import { admin_item_inventories_path } from "routes";
import { IMaskInput } from "react-imask";

type ItemInventoryFormProps = {
  show: boolean;
};

const ItemInventoryForm = ({
  show,
  onClose,
  onSave,
  warehouses = [],
  warehouse_id,
  inventoryItem,
  actions,
  adjustment_reasons,
  isItemsLoading,
  items,
  itemInventory
}: ItemInventoryFormProps): JSX.Element => {
  const defaultWarehouses = warehouse_id ? [parseFloat(warehouse_id)] : [];
  const {
    control,
    register,
    formState: { isSubmitting, isValidating, errors },
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset
  } = useForm({
    mode: "onChange",
    defaultValues: { inventory_item_id: "", warehouses: defaultWarehouses },
    resolver: yupResolver(ItemInventorySchema)
  });
  const action = watch("action");
  const adjustment = watch("adjustment");
  const [currentQuantity, setCurrentQuantity] = React.useState(0);
  const [adjustmentReason, setAdjustmentReason] = React.useState("");

  React.useEffect(() => {
    reset({}, { keepDefaultValues: true, keepErrors: false });
    if (!show) {
      setValue("inventory_item_id", "");
      setValue("quantity", 0);
      setCurrentQuantity(0);
    } else {
      setValue("inventory_item_id", inventoryItem?.id);
      setCurrentQuantity(parseFloat(inventoryItem?.available_quantity || 0));
    }
    setAdjustmentReason("");
  }, [show]);

  const save = async (data) => {
    try {
      const response = await api.post(admin_item_inventories_path(), { adjustment: data });
      onSave({ ...response.data, action, adjustment });
    } catch (error) {
      console.log(error);
    }
  };

  const updateAdjustments = (newAction, adjustedQuantity) => {
    if (newAction == Action.Add) {
      setValue("quantity", currentQuantity + parseFloat(adjustedQuantity));
    } else if (newAction == Action.Deduct) {
      setValue("quantity", currentQuantity - parseFloat(adjustedQuantity));
    } else {
      setValue("quantity", currentQuantity);
    }
  };

  return (
    <React.StrictMode>
      <Modal size="lg" centered show={show}>
        <Modal.Header>{`Update Inventory: ${
          inventoryItem ? inventoryItem.item_code : warehouses?.find((warehouse) => warehouse.id == warehouse_id)?.name
        }`}</Modal.Header>
        <Modal.Body>
          <Form>
            <Card.Body>
              <Container>
                <Row>
                  <Col>
                    <Controller
                      name="inventory_item_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          label="Inventory Item"
                          isLoading={isItemsLoading}
                          placeholder="Select an inventory item."
                          required
                          noOptionsMessage={() => "No available inventory items."}
                          options={items}
                          getOptionLabel={(option) => `${option.code} - ${option.name}`}
                          getOptionValue={(option) => option.id}
                          onChange={(newOption) => {
                            field.onChange(newOption?.id);
                            setCurrentQuantity(+itemInventory?.find((inv) => inv.id == newOption?.id)?.available_quantity || 0);
                            setValue("adjustment", "");
                            setValue("quantity", 0);
                          }}
                          value={items?.find((item) => item.id == field.value) || ""}
                          errorMessage={errors.inventory_item_id?.message}
                          disabled={inventoryItem?.id}
                        />
                      )}
                    />
                  </Col>
                  <Input label="Current Quantity" value={currentQuantity.toLocaleString()} readOnly={true} />
                </Row>
                <Row>
                  <Col>
                    <Controller
                      name="warehouses"
                      control={control}
                      render={({ field }) => (
                        <Select
                          label="Warehouse"
                          placeholder="Select a warehouse"
                          required
                          noOptionsMessage={() => "No available warehouse."}
                          options={warehouses}
                          getOptionLabel={(option) => option.name}
                          getOptionValue={(option) => option.id}
                          onChange={(newOption, actionMeta) => {
                            if (actionMeta.action == "select-option") {
                              setValue("warehouses", [...getValues("warehouses"), actionMeta.option.id]);
                            } else if (actionMeta.action == "remove-value") {
                              setValue(
                                "warehouses",
                                getValues("warehouses")?.filter((value) => value != actionMeta.removedValue.id)
                              );
                            } else {
                              setValue("warehouses", []);
                            }
                          }}
                          value={warehouses?.filter((warehouse) => field.value.includes(warehouse.id)) || []}
                          errorMessage={errors.warehouses?.message}
                          isMulti
                          disabled={warehouse_id}
                        />
                      )}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Controller
                      name="action"
                      control={control}
                      render={({ field }) => (
                        <Select
                          label="Action"
                          placeholder="Select an action"
                          required
                          noOptionsMessage={() => "No available actions."}
                          options={actions}
                          getOptionLabel={(option) => option.label}
                          getOptionValue={(option) => option.value}
                          onChange={(newOption) => {
                            field.onChange(newOption?.value);
                            updateAdjustments(newOption?.value, getValues("adjustment") || 0);
                          }}
                          value={actions?.find((action) => action.value == field.value) || null}
                          errorMessage={errors.action?.message}
                          disabled={adjustmentReason == "INITIAL"}
                        />
                      )}
                    />
                  </Col>
                  <Col>
                    <Controller
                      name="adjustment_reason_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          label="Adjustment Reason"
                          placeholder="Select a reason"
                          required
                          noOptionsMessage={() => "No available adjustment reason."}
                          options={adjustment_reasons}
                          getOptionLabel={(option) => option.name}
                          getOptionValue={(option) => option.id}
                          onChange={(newOption, actionMeta) => {
                            field.onChange(newOption?.id);

                            if (actionMeta.action == "select-option") {
                              if (newOption?.code == "INITIAL") setValue("action", "add");
                              setAdjustmentReason(newOption?.code);
                            } else {
                              setValue("action", "");
                              setAdjustmentReason("");
                            }
                          }}
                          value={adjustment_reasons?.find((adjustment_reason) => adjustment_reason.id == field.value) || ""}
                          errorMessage={errors.adjustment_reason_id?.message}
                        />
                      )}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Controller
                      name="adjustment"
                      control={control}
                      render={({ field }) => (
                        <FieldGroup label="Adjustment" required errorMessage={errors?.adjustment?.message}>
                          <IMaskInput
                            mask={Number}
                            padFractionalZeros={true}
                            thousandsSeparator=","
                            radix="."
                            value={field.value?.toString()}
                            unmask={true}
                            inputRef={field.ref}
                            onAccept={(value) => {
                              field.onChange(value);

                              if (value) {
                                updateAdjustments(action, parseFloat(value));
                              } else {
                                updateAdjustments(action, 0);
                              }
                            }}
                            className="field"
                            placeholder="0"
                            min={0}
                            max={9999999}
                          />
                        </FieldGroup>
                      )}
                    />
                  </Col>
                  <Col>
                    <Input {...register("quantity")} label="New Quantity" placeholder="0.0" readOnly={true} />
                  </Col>
                </Row>
              </Container>
            </Card.Body>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              onClose();
            }}
            icon="times"
            title="Close"
          />
          <Button
            className="button--brand"
            icon="save"
            title="Save"
            disabled={isSubmitting || isValidating}
            onClick={handleSubmit(
              async (data) => await save(data),
              (data) => console.log(data)
            )}
          />
        </Modal.Footer>
      </Modal>
    </React.StrictMode>
  );
};

export default ItemInventoryForm;
